import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/runtime/SimplePage.tsx
// A reusable runtime UI shell for builder-created plugins. Templates that just
// want "show a list of reviews matching X, with a button to do Y" use this so
// they don't all reinvent the same page layout.
import { useEffect, useMemo, useState } from 'react';
import { useReviews, useDisputes, useReplyToReview, useAI, useToast } from '../../sdk';
import { Button } from '../../../ui/Button';
import { SkeletonText } from '../../../ui/Skeleton';
import { EmptyState } from '../../../ui/EmptyState';
export function SimplePage(props) {
    const source = props.source ?? 'reviews';
    const reviews = source !== 'disputes' ? useReviews({ limit: props.limit ?? 50 }) : null;
    const disputes = source !== 'reviews' ? useDisputes() : null;
    const replyToReview = useReplyToReview();
    const generate = useAI();
    const toast = useToast();
    const [busy, setBusy] = useState({});
    const items = useMemo(() => {
        const reviewItems = reviews?.reviews
            ? reviews.reviews.filter(r => (props.filterReview ? props.filterReview(r) : true))
            : [];
        const disputeItems = disputes?.disputes
            ? disputes.disputes.filter(d => (props.filterDispute ? props.filterDispute(d) : true))
            : [];
        return [
            ...reviewItems.map(r => ({ kind: 'review', item: r })),
            ...disputeItems.map(d => ({ kind: 'dispute', item: d })),
        ];
    }, [reviews?.reviews, disputes?.disputes, props.filterReview, props.filterDispute]);
    async function runAction(rowKey, item) {
        if (!props.onAction)
            return;
        setBusy(b => ({ ...b, [rowKey]: true }));
        try {
            const helpers = {
                reply: replyToReview,
                ai: async (prompt, system) => {
                    const r = await generate({
                        messages: [{ role: 'user', content: prompt }],
                        system,
                        max_tokens: 240,
                        prompt_id: 'plugin:builder:simplepage',
                    });
                    return r.text;
                },
                toast,
            };
            await props.onAction(item, helpers);
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : 'Something went wrong.';
            toast.error(msg);
        }
        finally {
            setBusy(b => ({ ...b, [rowKey]: false }));
        }
    }
    const loading = (source !== 'disputes' && reviews?.reviews === null)
        || (source !== 'reviews' && disputes?.disputes === null);
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10", children: [_jsxs("header", { className: "mb-6", children: [_jsx("h1", { className: "text-2xl font-semibold tracking-tight text-slate-900", children: props.title }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: props.subtitle })] }), props.banner && _jsx("div", { className: "mb-4", children: props.banner }), loading
                ? _jsx(SkeletonText, { lines: 4 })
                : items.length === 0
                    ? _jsx(EmptyState, { title: props.emptyTitle ?? 'Nothing here yet', body: props.emptyBody ?? "When something matches, it'll appear here." })
                    : (_jsx("ul", { className: "space-y-3", children: items.map(({ kind, item }, i) => {
                            const key = `${kind}-${'id' in item ? item.id : i}`;
                            if (kind === 'review') {
                                const r = item;
                                return (_jsxs("li", { className: "rounded-2xl bg-white p-4 ring-1 ring-slate-200", children: [_jsxs("p", { className: "text-sm font-medium text-slate-900", children: [r.author_name ?? 'Anonymous', " \u00B7 ", r.rating ?? '—', "\u2605 \u00B7 ", r.platform] }), _jsx("p", { className: "mt-1 text-sm text-slate-600", children: r.body }), props.actionLabel && props.onAction && (_jsx("div", { className: "mt-3", children: _jsx(Button, { size: "sm", disabled: !!busy[key], onClick: () => runAction(key, r), children: busy[key] ? 'Working…' : props.actionLabel }) }))] }, key));
                            }
                            const d = item;
                            return (_jsxs("li", { className: "rounded-2xl bg-white p-4 ring-1 ring-slate-200", children: [_jsxs("p", { className: "text-sm font-medium text-slate-900", children: ["Dispute \u00B7 ", d.status] }), _jsx("p", { className: "mt-1 text-sm text-slate-600", children: d.claim_summary }), props.actionLabel && props.onAction && (_jsx("div", { className: "mt-3", children: _jsx(Button, { size: "sm", disabled: !!busy[key], onClick: () => runAction(key, d), children: busy[key] ? 'Working…' : props.actionLabel }) }))] }, key));
                        }) }))] }));
}
/** Auto-tick effect: re-renders SimplePage when reviews/disputes change in the
 *  background. Tiny shim so templates needing periodic refresh can opt in. */
export function useAutoRefresh(refresh, ms = 60_000) {
    useEffect(() => {
        const t = setInterval(refresh, ms);
        return () => clearInterval(t);
    }, [refresh, ms]);
}
